import React, { Dispatch, SetStateAction, useState } from 'react';
import { FilterParams } from '../../../../../interfaces/Filter';
import { DatePicker, DatePickerProps, Dropdown, MenuProps, Select } from 'antd';
import { FilterIcon } from '../../../../../components/icons';
import Button from '../../../../../components/button/Button';
import CustomModal from '../../../../../components/CustomModal';
import { dateRangeOptions, getDateRange } from '../schema';
import dayjs from 'dayjs';

const FilterSingleAccounts = ({
    transactionFilterOptions,
    setTransactionFilterOptions
}: {
    transactionFilterOptions: FilterParams;
    setTransactionFilterOptions: Dispatch<SetStateAction<FilterParams>>;
}) => {
    const [tempFilterOptions, setTempFilterOptions] = useState<
        FilterParams & { dateRange?: string }
    >(transactionFilterOptions);

    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);

    const onChangeFrom: DatePickerProps['onChange'] = (date) => {
        setTempFilterOptions({
            ...tempFilterOptions,
            from: date?.format('YYYY-MM-DD HH:mm:ss')
        });
    };

    const onChangeTo: DatePickerProps['onChange'] = (date) => {
        setTempFilterOptions({
            ...tempFilterOptions,
            to: date?.format('YYYY-MM-DD HH:mm:ss')
        });
    };

    const handleButtonClick = () => {
        setDropdownVisible(!dropdownVisible);
    };

    const handleApply = () => {
        setTransactionFilterOptions(tempFilterOptions);
        setDropdownVisible(false);
        setModalVisible(false);
    };

    const items = [
        {
            key: '1',
            label: (
                <p className='text-ebony text-base leading-[20px] pt-4 '>
                    Filter
                </p>
            )
        },
        {
            key: '2',
            label: (
                <div>
                    <label className='text-sm font-medium text-steelBlue block mb-2'>
                        Date Range
                    </label>

                    <Select
                        className='h-[40px] w-full'
                        onChange={(value: string) => {
                            const { startDate, endDate } = getDateRange(
                                value as any
                            );

                            setTempFilterOptions({
                                ...tempFilterOptions,
                                from: dayjs(startDate).format(
                                    'YYYY-MM-DD HH:mm:ss'
                                ),
                                to: dayjs(endDate).format(
                                    'YYYY-MM-DD HH:mm:ss'
                                ),
                                dateRange: value
                            });
                        }}
                        value={tempFilterOptions?.dateRange}
                        options={dateRangeOptions}
                        size='large'
                    />
                </div>
            )
        },

        {
            key: '4',

            label: (
                <div className='mt-[16px] lg:mt-0'>
                    <label className='text-sm font-medium text-steelBlue  block mb-2'>
                        Date
                    </label>
                    <div className='flex items-center gap-[12px]'>
                        <DatePicker
                            placeholder='Start Date'
                            value={
                                tempFilterOptions.from
                                    ? (dayjs(tempFilterOptions.from) as any)
                                    : null
                            }
                            onChange={onChangeFrom}
                            format='YYYY-MM-DD'
                            style={{ width: '100%', height: '40px' }}
                            disabled={tempFilterOptions.dateRange !== 'custom'}
                        />

                        <DatePicker
                            value={
                                tempFilterOptions.to
                                    ? (dayjs(tempFilterOptions.to) as any)
                                    : null
                            }
                            format='YYYY-MM-DD'
                            placeholder='End Date'
                            onChange={onChangeTo}
                            style={{ width: '100%', height: '40px' }}
                            disabled={tempFilterOptions.dateRange !== 'custom'}
                        />
                    </div>
                </div>
            )
        },

        {
            key: '6',
            label: (
                <div className='flex justify-between py-4'>
                    <Button
                        text='Cancel'
                        onClick={() => setDropdownVisible(false)}
                        variant='secondaryOutline'
                        size='sm'
                    />
                    <div className='flex gap-[16px]'>
                        <button
                            className='text-steelBlue text-xs leading-[20px]'
                            onClick={() => {
                                setTransactionFilterOptions({
                                    page: 0,
                                    size: 20
                                });
                                setTempFilterOptions({ page: 0, size: 20 });
                            }}
                        >
                            Reset
                        </button>
                        <Button
                            text='Apply Filter'
                            onClick={handleApply}
                            size='sm'
                        />
                    </div>
                </div>
            )
        }
    ];

    const dropdownItems = items as MenuProps['items'];

    return (
        <>
            <div className='hidden lg:block'>
                <Dropdown
                    placement='bottomRight'
                    autoAdjustOverflow={false}
                    trigger={['click']}
                    open={dropdownVisible}
                    dropdownRender={() => (
                        <div
                            className='w-[375px] rounded-lg border border-appcolorgrey shadow-lg p-4 bg-white'
                            onClick={(e) => e.stopPropagation()} // ⛔ prevents auto close
                        >
                            <p className='text-ebony text-base leading-[20px] pt-4'>
                                Filter
                            </p>

                            <div className='mt-4'>
                                <label className='text-sm font-medium text-steelBlue block mb-2'>
                                    Date Range
                                </label>
                                <Select
                                    className='h-[40px] w-full'
                                    onChange={(value: string) => {
                                        const {
                                            startDate,
                                            endDate
                                        } = getDateRange(value as any);

                                        setTempFilterOptions({
                                            ...tempFilterOptions,
                                            from: dayjs(startDate).format(
                                                'YYYY-MM-DD HH:mm:ss'
                                            ),
                                            to: dayjs(endDate).format(
                                                'YYYY-MM-DD HH:mm:ss'
                                            ),
                                            dateRange: value
                                        });
                                    }}
                                    value={tempFilterOptions?.dateRange}
                                    options={dateRangeOptions}
                                    size='large'
                                />
                            </div>

                            <div className='mt-[16px]'>
                                <label className='text-sm font-medium text-steelBlue block mb-2'>
                                    Date
                                </label>
                                <div className='flex items-center gap-[12px]'>
                                    <DatePicker
                                        placeholder='Start Date'
                                        value={
                                            tempFilterOptions.from
                                                ? (dayjs(
                                                      tempFilterOptions.from
                                                  ) as any)
                                                : null
                                        }
                                        onChange={onChangeFrom}
                                        format='YYYY-MM-DD'
                                        style={{
                                            width: '100%',
                                            height: '40px'
                                        }}
                                        disabled={
                                            tempFilterOptions.dateRange !==
                                            'custom'
                                        }
                                    />

                                    <DatePicker
                                        value={
                                            tempFilterOptions.to
                                                ? (dayjs(
                                                      tempFilterOptions.to
                                                  ) as any)
                                                : null
                                        }
                                        format='YYYY-MM-DD'
                                        placeholder='End Date'
                                        onChange={onChangeTo}
                                        style={{
                                            width: '100%',
                                            height: '40px'
                                        }}
                                        disabled={
                                            tempFilterOptions.dateRange !==
                                            'custom'
                                        }
                                    />
                                </div>
                            </div>

                            <div className='flex justify-between py-4 mt-4'>
                                <Button
                                    text='Cancel'
                                    onClick={() => setDropdownVisible(false)}
                                    variant='secondaryOutline'
                                    size='sm'
                                />
                                <div className='flex gap-[16px]'>
                                    <button
                                        className='text-steelBlue text-xs leading-[20px]'
                                        onClick={() => {
                                            setTransactionFilterOptions({
                                                page: 0,
                                                size: 20
                                            });
                                            setTempFilterOptions({
                                                page: 0,
                                                size: 20
                                            });
                                        }}
                                    >
                                        Reset
                                    </button>
                                    <Button
                                        text='Apply Filter'
                                        onClick={handleApply} // this will close it
                                        size='sm'
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                >
                    <Button
                        leftIcon={<FilterIcon />}
                        text='Filter'
                        variant='secondaryOutline'
                        onClick={handleButtonClick}
                        size='sm'
                    />
                </Dropdown>
            </div>

            <div className='lg:hidden w-[100vw]'>
                <CustomModal
                    isModalOpen={modalVisible}
                    onClose={() => setModalVisible(false)}
                    hideCloseIcon
                >
                    <div className='w-[85vw] h-[45vh]'>
                        {items?.map((item) => item?.label)}
                    </div>
                </CustomModal>

                <Button
                    leftIcon={<FilterIcon />}
                    text='Filter'
                    variant='tertiary'
                    onClick={() => setModalVisible(true)}
                    size='sm'
                />
            </div>
        </>
    );
};

export default FilterSingleAccounts;
